import { lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import {
    CardGrid,
    ContentSection,
    LinkRow,
    PageFrame,
    TruthStateGrid,
} from '../components/PageBlocks';
import { CLAIM_TRUTH_CLASSES } from '../data/page-config.mjs';
import { CURRENT_STAGE_NOTE } from '../data/site.mjs';

const AvenBoxScene = lazy(() => import('../components/three/AvenBoxScene'));

const reserveLabel = 'Reserve with \u20AC1 deposit';

const AvenBoxPage = () => {
    return (
        <PageFrame>
            <h1
                style={{
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    padding: 0,
                    margin: -1,
                    overflow: 'hidden',
                    clip: 'rect(0,0,0,0)',
                    whiteSpace: 'nowrap',
                    border: 0,
                }}
            >
                AvenBox — Sovereign AI Control System
            </h1>

            {/* ── Scroll-driven 3D device reveal ────────────────────────── */}
            <Suspense fallback={
                <div style={{ height: '100vh', background: 'var(--color-bg)' }} />
            }>
                <AvenBoxScene />
            </Suspense>

            {/* ── Reserve CTA — first thing after the scroll scene ─────── */}
            <ContentSection
                sectionId="reserve-track"
                eyebrow="Reservation — Open"
                title="Serious early-access interest only."
                intro="AvenBox is a working prototype. The reservation exists for people who have read the architecture and want a confirmed position in the first production run."
                borderTop
            >
                <div className="cta-row">
                    <Link to="/pricing" className="btn-pill btn-primary">
                        {reserveLabel}
                    </Link>
                    <Link to="/contact" className="btn-pill btn-secondary">
                        Join Waitlist
                    </Link>
                </div>
            </ContentSection>

            {/* ── Product definition ────────────────────────────────────── */}
            <ContentSection
                sectionId="product-definition"
                eyebrow="Product Definition"
                title="What AvenBox is in practical terms"
                intro="AvenBox is the product form of the company thesis: serious AI work should not require delegating control to cloud systems. It is portable by design, local-first by architecture, and built for continuity across surfaces."
            >
                <CardGrid
                    pattern="meta"
                    items={[
                        {
                            title: 'Portable private AI',
                            body: 'AvenBox brings private AI close to where the user is working — so sensitive context, private prompts, and real decisions stay under direct control rather than routed outward by default.',
                        },
                        {
                            title: 'A system with continuity',
                            body: 'AvenBox is designed to remain coherent when work moves — from direct device interaction to desk workflows and trusted environments — rather than forcing a restart at each surface.',
                        },
                        {
                            title: 'Control as the product',
                            body: 'The real product value is not the hardware. It is explicit authority over where intelligence runs, what stays local, and what may ever leave the trust boundary at all.',
                        },
                    ]}
                />
            </ContentSection>

            {/* ── Two modes ─────────────────────────────────────────────── */}
            <ContentSection
                sectionId="use-modes"
                eyebrow="Two Ways To Use It"
                title="Direct interaction and companion continuity"
                intro="AvenBox is designed for two modes of use that connect: direct on-device interaction, and companion continuity when work shifts to a desk or other trusted surface."
            >
                <div className="ed-mode-rows">
                    <div className="ed-mode-row">
                        <div className="ed-mode-row__num">01 / DIRECT</div>
                        <div className="ed-mode-row__body">
                            <h3 className="ed-mode-row__title">
                                Private at the <em>point of work</em>.
                            </h3>
                            <div className="ed-mode-row__aside">
                                <p className="ed-mode-row__lede">
                                    AvenBox handles immediate private interaction at the point of work — quick inputs, local AI queries, and routing decisions — without requiring a cloud connection to function.
                                </p>
                                <div className="ed-mode-row__meta">
                                    <div className="ed-mode-row__meta-item" data-tag="Prompt">
                                        Private prompts and context stay close to the user.
                                    </div>
                                    <div className="ed-mode-row__meta-item" data-tag="Intent">
                                        Deliberate interaction — designed for intent, not passive ambient listening.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="ed-mode-row">
                        <div className="ed-mode-row__num">02 / CONTINUITY</div>
                        <div className="ed-mode-row__body">
                            <h3 className="ed-mode-row__title">
                                Context that <em>survives movement</em>.
                            </h3>
                            <div className="ed-mode-row__aside">
                                <p className="ed-mode-row__lede">
                                    The companion layer keeps context coherent when work shifts from direct interaction to desk workflows and back again. Continuity is part of the product, not an accessory to it.
                                </p>
                                <div className="ed-mode-row__meta">
                                    <div className="ed-mode-row__meta-item" data-tag="Handoff">
                                        Context survives movement between direct use and the desk.
                                    </div>
                                    <div className="ed-mode-row__meta-item" data-tag="Trust">
                                        Trusted continuity matters more than surface novelty.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </ContentSection>

            {/* ── Execution paths ───────────────────────────────────────── */}
            <ContentSection
                sectionId="execution-modes"
                eyebrow="What It Can Coordinate"
                title="One control model across three paths"
                intro="AvenBox governs three defined execution paths. The user's authority over which path any task follows is the product — not the paths themselves."
            >
                <CardGrid
                    pattern="meta"
                    items={[
                        {
                            title: 'Local execution',
                            body: 'Local is the default path when work can remain within the immediate trusted setup. Private, fast, and not dependent on outside connectivity.',
                        },
                        {
                            title: 'Trusted extension',
                            body: 'Some tasks can extend to other systems the user controls — when more compute, more room, or a different environment is needed — without surrendering the control model.',
                        },
                        {
                            title: 'Controlled external path',
                            body: "Any outside path is explicit, constrained, and visible to the user — not the product's hidden default. Outside access is a user-governed choice, not a system assumption.",
                        },
                    ]}
                />
            </ContentSection>

            {/* ── Form factor rationale ─────────────────────────────────── */}
            <ContentSection
                sectionId="form-rationale"
                eyebrow="Why This Form Exists"
                title="Portable, not phone-like. Private, not isolated."
                intro="The product form is the answer to a specific problem: how to carry a serious AI control model into daily work without collapsing into the wrong category."
            >
                <CardGrid
                    pattern="meta"
                    items={[
                        {
                            title: 'Portable, not a phone replacement',
                            body: 'Portability matters because serious work does not happen in only one place. That does not make AvenBox a phone category product — the form follows the trust model, not a mobile-device template.',
                        },
                        {
                            title: 'Private, not cut off from workflow',
                            body: 'AvenBox keeps private AI close without cutting the user off from the tools and environments they already trust. Privacy and continuity are both requirements, not a trade-off.',
                        },
                        {
                            title: 'Purpose-built, not novelty-built',
                            body: 'The form factor exists to carry a different trust model for AI into daily work — not to generate gadget fascination or product-launch theater.',
                        },
                    ]}
                />
            </ContentSection>

            {/* ── Specs ─────────────────────────────────────────────────── */}
            <ContentSection
                sectionId="specs"
                eyebrow="Specifications"
                title="Directional specifications"
                intro="Reference specifications for the current production direction. Final numbers may shift before launch as component costs settle."
            >
                <div className="ed-specs">
                    <div className="ed-specs__row">
                        <div className="ed-specs__num">01</div>
                        <div className="ed-specs__key">Compute</div>
                        <div className="ed-specs__value">
                            <strong>Local-first AI core</strong>
                            <span>Dedicated on-device inference. No cloud dependency to operate.</span>
                        </div>
                    </div>
                    <div className="ed-specs__row">
                        <div className="ed-specs__num">02</div>
                        <div className="ed-specs__key">Performance</div>
                        <div className="ed-specs__value">
                            <strong>Up to 157 TOPS</strong>
                            <span>On-device AI performance targeted for local-first inference workloads.</span>
                        </div>
                    </div>
                    <div className="ed-specs__row">
                        <div className="ed-specs__num">03</div>
                        <div className="ed-specs__key">Memory</div>
                        <div className="ed-specs__value">
                            <strong>16 GB unified</strong>
                            <span>Working memory shared across the orchestration and inference layers.</span>
                        </div>
                    </div>
                    <div className="ed-specs__row">
                        <div className="ed-specs__num">04</div>
                        <div className="ed-specs__key">Storage</div>
                        <div className="ed-specs__value">
                            <strong>512 GB NVMe</strong>
                            <span>Encrypted local storage. No telemetry upload by default.</span>
                        </div>
                    </div>
                    <div className="ed-specs__row">
                        <div className="ed-specs__num">05</div>
                        <div className="ed-specs__key">Connectivity</div>
                        <div className="ed-specs__value">
                            <strong>Wi-Fi 6E, Bluetooth 5.3, USB-C, USB-A</strong>
                            <span>Trusted extension paths over local network. External access explicit and bounded.</span>
                        </div>
                    </div>
                    <div className="ed-specs__row">
                        <div className="ed-specs__num">06</div>
                        <div className="ed-specs__key">Form factor</div>
                        <div className="ed-specs__value">
                            <strong>16 × 7.7 × 1.8 cm</strong>
                            <span>Palm-scale enclosure. Portable, desk-coherent — carries between direct use and desk workflows without losing context.</span>
                        </div>
                    </div>
                    <div className="ed-specs__row">
                        <div className="ed-specs__num">07</div>
                        <div className="ed-specs__key">Trust boundary</div>
                        <div className="ed-specs__value">
                            <strong>Architecture-level</strong>
                            <span>Routing decisions enforced in the control layer, not in user-configurable settings.</span>
                        </div>
                    </div>
                </div>
                <p className="ed-specs__note">All values directional — subject to change before launch.</p>
            </ContentSection>

            {/* ── Truth state ───────────────────────────────────────────── */}
            <ContentSection
                sectionId="truth-state"
                eyebrow="Truth State"
                title="What is real, what is current prototype, and what is later"
            >
                <TruthStateGrid
                    intro={CURRENT_STAGE_NOTE}
                    items={[
                        {
                            truthClass: CLAIM_TRUTH_CLASSES.REAL_NOW,
                            title: 'Core control logic',
                            description:
                                'The real foundation is the control-layer model itself: local-first execution, defined trust boundaries, and continuity as a core system property.',
                        },
                        {
                            truthClass: CLAIM_TRUTH_CLASSES.PROTOTYPE_NOW,
                            title: 'Current interaction and continuity',
                            description:
                                'Current interaction flows, companion continuity, and the full product experience exist in prototype form. They are real and active — and still being shaped toward production-grade behavior.',
                        },
                        {
                            truthClass: CLAIM_TRUTH_CLASSES.ROADMAP,
                            title: 'Finished product maturity',
                            description:
                                'Finished consumer hardware, broader ecosystem depth, and later platform polish are part of the production roadmap. They are not yet complete.',
                        },
                    ]}
                />
            </ContentSection>

            {/* ── Final CTA ─────────────────────────────────────────────── */}
            <ContentSection
                sectionId="final-reserve"
                eyebrow="Reserve"
                title="The reservation track is open now"
                featured
                intro="A €1 deposit records serious early-access interest in a controlled pre-production position. It is not a purchase of a finished product — public pricing and timing are directional."
            >
                <div className="cta-row">
                    <Link to="/pricing" className="btn-pill btn-primary">
                        {reserveLabel}
                    </Link>
                    <Link to="/contact" className="btn-pill btn-secondary">
                        Join Waitlist
                    </Link>
                </div>
            </ContentSection>

            <ContentSection sectionId="next-step" eyebrow="Next Step" title="Evaluate the product from the right angles">
                <LinkRow
                    links={[
                        { label: 'How It Works', to: '/technology' },
                        { label: 'Security & Privacy', to: '/security' },
                        { label: 'Pricing & Reservation', to: '/pricing' },
                        { label: 'FAQ', to: '/faq' },
                    ]}
                />
            </ContentSection>

        </PageFrame>
    );
};

export default AvenBoxPage;
